/**
 * InstallBanner.tsx
 * Shows a "Install Jötr on your device" banner for first-time visitors.
 * Only shows when browser supports PWA install.
 * Never shows again after dismissed or installed.
 */
import React, { useEffect, useState } from 'react';
import { X, Download } from 'lucide-react';

const STORAGE_KEY = 'jötr_install_dismissed';

const InstallBanner: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed or installed
    if (localStorage.getItem(STORAGE_KEY)) return;

    // Don't show if already running as PWA
    const isPWA = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true;
    if (isPWA) return;

    // Listen for browser install prompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShow(true);
    };

    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      localStorage.setItem(STORAGE_KEY, '1');
    }
    setShow(false);
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setShow(false);
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      zIndex: 99999, width: 'min(90vw, 420px)',
      background: '#1A1814', borderRadius: 16,
      padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12,
      boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
      animation: 'slideUp .35s ease',
    }}>
      <style>{`@keyframes slideUp{from{opacity:0;transform:translateX(-50%) translateY(20px)}to{opacity:1;transform:translateX(-50%) translateY(0)}}`}</style>

      {/* Icon */}
      <div style={{ width: 40, height: 40, borderRadius: 11, background: 'linear-gradient(145deg,#FFD88A,#C4811A)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <svg width="18" height="18" viewBox="0 0 180 180" fill="none">
          <g transform="translate(90,88) rotate(-35)">
            <rect x="-12" y="-38" width="24" height="58" rx="7" fill="white"/>
            <rect x="-12" y="-38" width="24" height="16" rx="7" fill="#F08030" opacity="0.9"/>
            <polygon points="-12,20 12,20 0,44" fill="white" opacity="0.9"/>
          </g>
          <circle cx="104" cy="102" r="42" fill="none" stroke="white" strokeWidth="13" opacity="0.95"/>
          <g transform="translate(126,124) rotate(45)">
            <rect x="-6" y="-4" width="30" height="10" rx="5" fill="white"/>
          </g>
        </svg>
      </div>

      {/* Text */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 13, color: '#FFFFFF', marginBottom: 2 }}>
          Install Jötr on your device
        </p>
        <p style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: '#7A7570', lineHeight: 1.4 }}>
          Works offline. Opens like a real app.
        </p>
      </div>

      {/* Install button */}
      <button onClick={install} style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '8px 14px', borderRadius: 9, border: 'none',
        background: 'linear-gradient(135deg,#F5A623,#C4811A)',
        color: '#fff', fontWeight: 700, fontSize: 12,
        fontFamily: "'DM Sans',sans-serif", cursor: 'pointer',
        flexShrink: 0, whiteSpace: 'nowrap',
      }}>
        <Download size={12}/> Install
      </button>

      {/* Dismiss */}
      <button onClick={dismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#7A7570', padding: 4, display: 'flex', flexShrink: 0 }}>
        <X size={15}/>
      </button>
    </div>
  );
};

export default InstallBanner;